import React from 'react';
import { LuMic, LuCircleStop, LuRotateCcw, LuLoaderCircle, LuAudioLines } from 'react-icons/lu';
import XiLogo from './ui/XiLogo';
import { VoiceEnrollErrorBanner } from './VoiceEnrollStates';

/**
 * Voice enrollment — recording step.
 *
 * Presentation only: `mode`, `recording`, `timer`, `segmentCount` and all
 * callbacks are owned by `VoiceEnroll.jsx` / `VoiceEnrollStaff.jsx`. The Xi
 * layer only changes how those states are shown.
 */
export default function VoiceEnrollRecording({
  name,
  mode,
  recording,
  timer,
  segmentCount,
  onRecord,
  onStop,
  onContinue,
  onReset,
  saving,
  error = '',
}) {
  const totalSegments = 5;
  const showProgress = recording || segmentCount > 0;

  return (
    <div className="xi-public xi-enroll">
      <div className="xi-public__veil" aria-hidden="true" />
      <div className="xi-public__body">
        <div className="xi-enroll__brand">
          <XiLogo size="sm" tagline />
        </div>

        <header className="xi-enroll__head">
          <span className="xi-eyebrow">Voice enrollment</span>
          <h1>Voice recording</h1>
          <p className="xi-muted">
            Hello{name ? <strong> {name}</strong> : null} — record your voice for emotional
            assessment.
          </p>
        </header>

        <div className="xi-auth-card xi-enroll__card">
          <VoiceEnrollErrorBanner message={error} />

          <div className="xi-card__head">
            <div>
              <h2 className="xi-card__title">Choose a recording mode</h2>
              <p className="xi-card__sub">Pick the length that suits your session.</p>
            </div>
            <span className="xi-badge xi-badge--brand" aria-hidden="true">
              <LuAudioLines /> Audio
            </span>
          </div>

          <div className="xi-enroll__modes">
            <button
              type="button"
              className={`xi-enroll__mode${mode === '40s' ? ' is-selected' : ''}`}
              onClick={() => onRecord('40s')}
              disabled={recording}
            >
              <span className="xi-enroll__mode-icon" aria-hidden="true">
                <LuMic />
              </span>
              <span className="xi-enroll__mode-text">
                <strong>40 seconds</strong>
                <span>Continuous — record your voice in one go.</span>
              </span>
            </button>

            <button
              type="button"
              className={`xi-enroll__mode${mode === '8s' ? ' is-selected' : ''}`}
              onClick={() => onRecord('8s')}
              disabled={recording}
            >
              <span className="xi-enroll__mode-icon" aria-hidden="true">
                <LuMic />
              </span>
              <span className="xi-enroll__mode-text">
                <strong>8 seconds quick check</strong>
                <span>Five short sections, one at a time.</span>
              </span>
            </button>
          </div>
        </div>

        {showProgress && (
          <div className="xi-auth-card xi-enroll__card">
            <div className="xi-card__head">
              <div>
                <h2 className="xi-card__title">
                  {mode === '40s'
                    ? 'Recording in progress'
                    : `Recording progress (${segmentCount}/${totalSegments} segments)`}
                </h2>
                <p className="xi-card__sub">
                  {mode === '40s'
                    ? 'Speak naturally until the timer ends.'
                    : 'Complete each segment to finish enrollment.'}
                </p>
              </div>
            </div>

            {mode === '8s' && (
              <div className="xi-enroll__segments" aria-hidden="true">
                {Array.from({ length: totalSegments }).map((_, i) => {
                  const done = i < segmentCount;
                  const active = i === segmentCount && recording;
                  return (
                    <span
                      key={i}
                      className={`xi-enroll__segment${done ? ' is-done' : ''}${
                        active ? ' is-active' : ''
                      }`}
                    />
                  );
                })}
              </div>
            )}

            {mode === '8s' && (
              <p className="xi-enroll__status" aria-live="polite">
                {segmentCount < totalSegments
                  ? recording
                    ? 'Recording segment…'
                    : `Segment ${segmentCount + 1} of ${totalSegments} — continue when ready.`
                  : 'All five segments completed.'}
              </p>
            )}

            {recording && (
              <div className="xi-enroll__live" role="status" aria-live="polite">
                <span className="xi-enroll__pulse" aria-hidden="true">
                  <LuMic />
                </span>
                <div className="xi-enroll__timer xi-num">{timer}s</div>
                <p className="xi-enroll__live-label">Recording in progress…</p>
                <button type="button" className="xi-btn xi-btn--danger" onClick={onStop}>
                  <LuCircleStop aria-hidden="true" />
                  Stop recording
                </button>
              </div>
            )}

            {mode === '8s' && !recording && segmentCount < totalSegments && (
              <div className="xi-enroll__actions">
                <button type="button" className="xi-btn xi-btn--primary" onClick={onContinue}>
                  <LuMic aria-hidden="true" />
                  {segmentCount === 0
                    ? 'Start recording'
                    : `Record segment ${segmentCount + 1}/${totalSegments}`}
                </button>
              </div>
            )}

            <div className="xi-enroll__foot">
              <button
                type="button"
                className="xi-btn xi-btn--ghost"
                onClick={onReset}
                disabled={saving}
              >
                <LuRotateCcw aria-hidden="true" />
                Reset and start over
              </button>
            </div>
          </div>
        )}

        {saving && (
          <div className="xi-auth-card xi-enroll__card xi-enroll__saving" role="status" aria-live="polite">
            <LuLoaderCircle className="xi-enroll__spin" aria-hidden="true" />
            <span>Saving recording…</span>
          </div>
        )}
      </div>
    </div>
  );
}
